<template>
<!-- :style="mfmHeight" -->
  <v-card color="primary" class="pa-2 subheading align-stretch" :style="{ height: cardHeight }">
    <div id="info-header">
      <span class="subheading">{{ $t('MFM') }}</span> {{ $t('Live Data') }}
    </div>
    <DataTable class="mfm-data-card"  :style="{ height: tableHeight }" :headers="computedMfmHeader" :items="mfmData"
      :isPaginationFalse="true">
      <template v-slot:firstCol="{ text }">
        <td>
          <v-layout row wrap align-center class="first-col-width">
            <div class="text__width">
              {{ text.name }}
            </div>
            <div>
              <v-icon
                :color="text.ac_power == undefined ? 'grey' : ( text.ac_power <= 0 ? 'red' : 'green' )"
                class="icon-class" size="10">circle</v-icon>
            </div>
          </v-layout>
        </td>
      </template>
    </DataTable>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import LiveDb from '@/services/LiveDb'
import { mapGettersMixin } from '@/util/mixin/index'

export default {
  mixins: [ mapGettersMixin ],
  props: {
    nameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    parameter: {
      type: Array,
      default: function() {
        return []
      }
    },
    parameterNameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    cardHeight: {
      type: String,
      default: 'calc(80vh - 600px)'
    },
    tableHeight: {
      type: String,
      default: 'calc(80vh - 661px)'
    }
  },
  data() {
    return {
      mfmHeaders: [],
      mfmData: [],
      liveMfmData: {},
      computedMfmData: []
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getRules']),
    availableDeviceList() {
      const accessConfig = this.getRules(['rule', 'access_config'])
      return accessConfig && accessConfig['live_device'] ? Object.keys(accessConfig['live_device']) : []
    },
    isEmAvailable() {
      return this.availableDeviceList.includes('Energy Meter')
    },
    isMfmAvailable() {
      return this.availableDeviceList.includes('MFM')
    },
    pxConvertToVh() {
      // Added height of header and their padding
      // And converting it to vh
      return 100 * 240 / window.innerHeight
    },
    mfmTableHeight() {
      if (this.isMfmAvailable && this.isEmAvailable) {
        return {
          height: `calc(100vh - (6vh + ${this.pxConvertToVh}vh + 33vh))`
        }
      } else if (this.isMfmAvailable && !this.isEmAvailable) {
        return {
          height: this.cardHeight
        }
      }
    },
    computedMfmHeader() {
      const cloneHeader = _.cloneDeep(this.mfmHeaders)
      return cloneHeader.map(el => {
        el['text'] = this.$t(el.text)
        return el
      })
    }
  },
  async mounted() {
    this.initialStateValues()
    await this.liveMfmDataHandler()
    this.setIntervalWithoutDelay(await this.getMFMGeneration, 60000)
    this.mqttHandler()
  },
  beforeDestroy() {
    this.$mqtt.unsubscribe(`live/${this.getSelectedPlantId}/mfm`)
  },
  methods: {
    initialStateValues() {
      this.initialHeader()
      this.mfmData = []
      this.liveMfmData = {}
      this.computedMfmData = []
    },
    isFloat(value) {
      if (
        typeof value === 'number' &&
        !Number.isNaN(value) &&
        !Number.isInteger(value)
      ) {
        return true;
      }

      return false;
    },
    toThreeDecimalPlaces(props) {
      if (props !== undefined && props !== null) {
        if (this.isFloat(Number(props))) {
          return props == 0 ? 0 : Number.parseFloat(props).toFixed(3)
        } else {
          return props
        }
      } else {
        return 'No Data'
      }
    },
    async getMFMGeneration() {
      await this.computedMfmDataHandler()

      const computedMap = this.computedMfmData.reduce((acc, curr) => {
        acc[curr.id] = curr
        return acc
      }, {})

      if (this.mfmData.length === 0) {
        const dataFromMqtt = Object.keys(this.parameterNameMap).map(idConfigured => {
          const newDataObj = {
            id: idConfigured,
            name: this.nameMap[idConfigured]
          }

          if (this.liveMfmData[idConfigured]) {
            for (const [key, value] of Object.entries(this.liveMfmData[idConfigured])) {
              if (key !== 'id' && key !== 'name') {
                newDataObj[key] = this.toThreeDecimalPlaces(value)
              }
            }
          }

          const computed = computedMap[idConfigured]
          if (computed) {
            newDataObj.export = this.toThreeDecimalPlaces(computed.export)
            newDataObj.import = this.toThreeDecimalPlaces(computed.import)
          }

          return newDataObj
        })
        
        this.mfmData = dataFromMqtt
        this.mapHeaderHandler()
      } else {
        this.mfmData.forEach(row => {
          const computed = computedMap[row.id]
          if (computed) {
            this.$set(row, 'export', this.toThreeDecimalPlaces(computed.export))
            this.$set(row, 'import', this.toThreeDecimalPlaces(computed.import))
          }
        })
      }
    },
    async liveMfmDataHandler() {
      let mfmParamJson = {}
      this.parameter.forEach(key => {
        mfmParamJson[key] = key
      })
      const mfmParam = Buffer.from(JSON.stringify(mfmParamJson)).toString('base64')
      // Live data query
      const response = await LiveDb.getLiveDeviceData(this.getSelectedPlantId, 'mfm', mfmParam)
      this.liveMfmData = response.data.message
    },
    async computedMfmDataHandler() {
      const mfmParam = Buffer.from(JSON.stringify({
        'active_total_export_cumulative': 'export',
        'active_total_import_cumulative': 'import'
      })).toString('base64')
      const response = await LiveDb.getGenerationData(this.getSelectedPlantId, 'mfm', mfmParam)
      this.computedMfmData = response.data.message
    },
    mapHeaderHandler() {
      const header = []
      this.parameter.forEach(param => {
        const ob = {}
        const name = this.parameterNameMap[this.mfmData[0].id][0].config[param].name
        const unit = this.parameterNameMap[this.mfmData[0].id][0].config[param].unit ? this.parameterNameMap[this.mfmData[0].id][0].config[param].unit : ''
        ob['text'] = `${name} ${unit ? `(${unit})` : ''}`
        ob['value'] = param
        ob['align'] = 'center'
        header.push(ob)
      })

      this.initialHeader()
      this.mfmHeaders = [...this.mfmHeaders, ...header]
    },
    mqttHandler() {
      this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/mfm`)
      if (this.mqttBound) return;
      this.mqttBound = true;
      this.$mqtt.on('message', (topic, message) => {
        if (topic.startsWith('live/')) {
          if (topic.endsWith('mfm')) {
            const mqttData = JSON.parse(message.toString()).deviceList[0]
            if (mqttData.ignore_status !== 1) {
              const index = this.mfmData.findIndex(obj => obj.id === mqttData.device_id)
              this.parameter.forEach(key => {
                this.mfmData[index][key] = this.toThreeDecimalPlaces(mqttData[key])
              })
            }
          }
        }
      })
      this.$mqtt.on('error', function(error) {
        console.log('ERROR: ', error)
      })

      this.$mqtt.on('offline', function() {
        console.log('offline')
      })

      this.$mqtt.on('reconnect', function() {
        console.log('reconnect')
      })
    },
    initialHeader() {
      this.mfmHeaders = [{
        text: 'MFM Name',
        align: 'left',
        sortable: false,
        value: 'name'
      },
      {
        text: 'Export(kWh)',
        align: 'center',
        value: 'export'
      },
      {
        text: 'Import(kWh)',
        align: 'center',
        value: 'import',
        sortable: false
      }
      ]
    }
  },
  watch: {
    async getSelectedPlantId(newVal, oldVal) {
      this.initialStateValues()
      this.$mqtt.unsubscribe(`live/${oldVal}/mfm`)
      await this.liveMfmDataHandler()
      this.setIntervalWithoutDelay(await this.getMFMGeneration, 60000)
      this.mqttHandler()
    }
  }
}
</script>

<style scoped>
.icon-class {
  margin-bottom: 2px;
  margin-left: 12px;
}

#info-header {
  padding-bottom: 16px;
}

#info-header span {
  border-bottom: 2px solid #417CDE;
  display: inline-block;
  vertical-align: top;
}

.first-col-width {
  width: 150px;
  word-wrap: break-word;
}

.text__width {
  width: 120px;
}

</style>
<style>
.mfm-data-card .v-table__overflow .v-table thead tr:first-child {
  position: sticky;
  top: 0px;
  z-index: 2;
  background: var(--v-primary-base);
  border-top: 1px solid #38405C;
}

.mfm-data-card .v-table__overflow .v-table tbody td:first-child,
table.v-table thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--v-primary-base);
  border-right: 1px solid #38405C;
}

</style>